import {
  useLoaderData,
  useRouteLoaderData,
  useSearchParams,
} from "react-router-dom";
import { Organization } from "../../../services/organization/organizationModel";
import { useTranslation } from "react-i18next";
import ProPaymentMethodForm from "./ProPaymentMethodForm";
import DisplaySubscription from "../../../components/DisplaySubscription";
import useReload from "../../../hooks/useReload";
import { subscriptionService } from "../../../services/subscription/subscriptionService";
import SubscribeDialog from "./SubscribeDialog";
import { useEffect, useMemo, useRef, useState } from "react";
import { ReturnButton } from "../../../components/ReturnButton";
import BuyCapsulesCreditsDialog from "./BuyCapsulesCreditsDialog";
import { toastsWithIntl } from "../../../services/toastService";
import { User } from "../../../services/user/userModel";
import { Capsule } from "../../../services/capsule/capsuleModel";

const { getOrganizationPortalLink } = subscriptionService();
const { toastSuccess } = toastsWithIntl(["organization"]);

// The subscription is activated by the Stripe webhook, which can lag a few
// seconds behind the Checkout redirect: poll the loader until it shows up.
const CHECKOUT_PENDING_RELOAD_INTERVAL_MS = 3000;
const CHECKOUT_PENDING_MAX_RELOADS = 10;

const SubscriptionPage = () => {
  const { loggedUser } = useRouteLoaderData("logged-user") as {
    loggedUser: User;
  };
  const { organization } = useRouteLoaderData("organization") as {
    organization: Organization;
  };
  const { capsules } = useLoaderData() as {
    capsules: Capsule[];
  };
  const { t } = useTranslation(["organization"]);
  const reload = useReload();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSubscribeDialog, setShowSubscribeDialog] = useState(false);
  const [showBuyCreditsDialog, setShowBuyCreditsDialog] = useState(false);
  const [awaitingActivation, setAwaitingActivation] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const remainingReloadsRef = useRef(CHECKOUT_PENDING_MAX_RELOADS);

  const invitation = useMemo(() => {
    return loggedUser.invitations?.[0] ?? null;
  }, [loggedUser]);

  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState !== "hidden") reload();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [reload]);

  useEffect(() => {
    const checkoutResult = searchParams.get("checkout");
    if (!checkoutResult) return;
    if (checkoutResult === "success") {
      toastSuccess("organization:subscription.checkout.SUCCESS", {
        id: "checkout-success",
      });
      setAwaitingActivation(true);
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const awaitingSubscriptionActivation =
    awaitingActivation && !organization.subscriptions?.[0];

  useEffect(() => {
    if (!awaitingSubscriptionActivation) return;

    const interval = setInterval(() => {
      if (remainingReloadsRef.current <= 0) {
        clearInterval(interval);
        return;
      }
      remainingReloadsRef.current -= 1;
      reload();
    }, CHECKOUT_PENDING_RELOAD_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [awaitingSubscriptionActivation, reload]);

  return (
    <div className="layout_content --bg--organization-dashboard">
      <div className="page-content container --wrap">
        <ReturnButton fallbackUrl="../.." />
        <h1 className="page_title">
          {t("organization:subscription.PAGE_TITLE")}
        </h1>

        <div className="lblock grid">
          <div className="card --compact --border--light">
            <div className="card_head --stretch--h">
              <h2 className="--txt--center">
                {t("organization:subscription.SUBSCRIPTION")}
              </h2>
            </div>
            <div className="card_body">
              {organization.subscriptions?.[0] ? (
                <DisplaySubscription
                  subscription={organization.subscriptions[0]}
                  cta={
                    <div className="cblock --txt--center">
                      <button
                        className="btn--2"
                        disabled={loadingPortal}
                        onClick={() => {
                          setLoadingPortal(true);
                          getOrganizationPortalLink(organization.id)
                            .then(({ url }) => {
                              window.location.href = url;
                            })
                            .catch(() => undefined)
                            .finally(() => setLoadingPortal(false));
                        }}
                      >
                        {loadingPortal
                          ? "..."
                          : t("organization:subscription.MANAGE_SUBSCRIPTION")}
                      </button>
                    </div>
                  }
                />
              ) : awaitingSubscriptionActivation ? (
                <div className="info">
                  {t("organization:subscription.checkout.PENDING_ACTIVATION")}
                </div>
              ) : (
                <div className="--txt--center">
                  <button
                    className="btn--1"
                    onClick={() => setShowSubscribeDialog(true)}
                  >
                    {t("organization:subscription.NEW_SUBSCRIPTION")}
                  </button>
                </div>
              )}
            </div>
          </div>
          {invitation && (
            <div className="card --compact --border--light --small-side-padding">
              <div className="card_head --stretch--h">
                <h2 className="--txt--center">
                  {t("organization:subscription.INVITATION")}
                </h2>
              </div>
              <div className="card_body">
                {(invitation.organization?.subscriptions?.length ?? 0) > 0 ? (
                  <div className="info --success">
                    {t(
                      "organization:subscription.INVITATION_VALID_SUB_DESCRIPTION",
                      {
                        replace: {
                          pro: invitation.organization?.legalName,
                        },
                      },
                    )}
                  </div>
                ) : (
                  <div className="info --warning">
                    {t(
                      "organization:subscription.INVITATION_EXPIRED_SUB_DESCRIPTION",
                      {
                        replace: {
                          pro: invitation.organization?.legalName,
                        },
                      },
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="card --compact --border--light">
            <div className="card_head --stretch--h">
              <h2 className="--txt--center">
                {t("organization:subscription.CAPSULES_CREDITS")}
              </h2>
            </div>
            <div className="card_body --txt--center">
              <div className="body--40 cblock">
                {t("organization:subscription.capsules-credits.CREDITS_LEFT")}
              </div>
              <div className="title--60">
                {organization.maxCapsulesCount - capsules.length}
              </div>
              <div className="cblock">
                <button
                  className="btn--3"
                  onClick={() => setShowBuyCreditsDialog(true)}
                >
                  {t("organization:subscription.capsules-credits.BUY")}
                </button>
              </div>
            </div>
          </div>
          {!!organization.stripeCustomer && (
            <div className="card --compact --border--light --small-side-padding">
              <div className="card_head --stretch--h">
                <h2 className="--txt--center">
                  {t("organization:subscription.PAYMENT_METHOD")}
                </h2>
              </div>
              <div className="card_body">
                <ProPaymentMethodForm organization={organization} />
              </div>
            </div>
          )}
        </div>
      </div>

      {showSubscribeDialog && (
        <SubscribeDialog
          organization={organization}
          onClose={() => setShowSubscribeDialog(false)}
        />
      )}
      {showBuyCreditsDialog && (
        <BuyCapsulesCreditsDialog
          onClose={() => setShowBuyCreditsDialog(false)}
          organization={organization}
        />
      )}
    </div>
  );
};

export default SubscriptionPage;
